<template>
  <div :class="['type-' + resource.type, 'tw-overflow-x-auto']">
    <author-toolbar
      v-if="loggedInUser && loggedInUser.capabilities.includes('publish_posts')"
      :post="resource"
    />
    <local-version-banner v-if="resource.local_version" :content="resource.local_version" />
    <div class="tw-mx-auto tw-flex tw-max-w-8xl tw-flex-wrap lg:tw-mt-12 lg:tw-justify-between">
      <!-- The following 2 divs are switched so that the most important info is first in the DOM this is for accessibility-->
      <div
        class="lg:flex-auto tw-order-2 tw-hidden tw-max-w-full tw-flex-auto tw-px-6 md:tw-px-10 lg:tw-block lg:tw-max-w-[37.5%] lg:tw-pl-0"
      >
        <resource-header :resource="resource" />
        <resource-page-plans-cta
          v-if="$isFlagOn('resource-page-cta')"
          :is-free-resource="resource.is_free"
        />
        <span v-if="resource.curriculums && resource.curriculums.length > 0" class="tw-mt-4">
          <h3>Curriculum</h3>
          <curriculum-list :curriculums="resource.curriculums" />
        </span>
      </div>

      <div
        class="tw-order-1 tw-max-w-full tw-flex-auto tw-px-6 md:tw-px-10 lg:tw-max-w-[57.5%] lg:tw-pr-0"
      >
        <plan-cta-pill v-if="isFreeOrGuestUser" :is-free="resource.is_free" />
        <image-gallery
          :name="resource.name"
          :type="resource.type.replace('-', ' ')"
          :images="resource.media"
          :event-params="resource.event_params"
        />
        <div class="tw-block lg:tw-hidden">
          <resource-header :resource="resource" />
          <resource-page-plans-cta
            v-if="$isFlagOn('resource-page-cta')"
            :is-free-resource="resource.is_free"
          />
        </div>
        <resource-content class="content" :resource="resource" />
        <resource-footer class="links" :resource="resource" />
        <comment-section
          class="section section-comments"
          :resource-id="resource.id"
          :comment-count="resource.comments"
          :resource-updates="resource.updates"
          :resource-link="$config.baseURL + resource.link"
          :resource-name="resource.name"
          :event-params="resource.event_params"
        />
      </div>
    </div>

    <tag-list
      class="tw-mx-auto tw-flex tw-max-w-8xl tw-flex-wrap tw-px-6 md:tw-p-10"
      :file-formats="resource.file_formats"
      :tags="resource.tags"
      :year-levels="resource.year_levels"
      :country="resource.country"
      title="You may also like"
    />

    <resources-list
      :load-more="true"
      :params="{ id: resource.id }"
      list-type="related"
      :parent-resource="resource"
    />
    <country-switcher
      class="section"
      :link="resource.link"
      :linked="resource.linked"
      :country="resource.country"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CommentSection from '~/components/comment/CommentSection.vue';
import ImageGallery from '~/components/resource/ImageGallery.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import ResourceContent from '~/components/resource/ResourceContent.vue';
import ResourceFooter from '~/components/resource/ResourceFooter.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import { ExperimentTypes } from '~/types/experiments';
import ResourcePagePlansCta from '~/components/resource/ResourcePagePlanCta.vue';
import ResourceHeader from '~/components/resource/ResourceHeader.vue';
import PlanCtaPill from '~/components/resource/new-resource-uplift/PlanCtaPill.vue';
import TagList from '~/components/resource/TagList.vue';
import CurriculumList from '~/components/resource/CurriculumList.vue';

export default {
  name: 'TeachingResource',
  components: {
    AuthorToolbar: () => import('~/components/AuthorToolbar.vue'),
    CommentSection,
    ImageGallery,
    LocalVersionBanner: () => import('~/components/resource/LocalVersionBanner.vue'),
    ResourcesList,
    ResourceHeader,
    ResourceContent,
    ResourceFooter,
    CountrySwitcher,
    ResourcePagePlansCta,
    PlanCtaPill,
    TagList,
    CurriculumList,
  },
  async asyncData({ app, store, route, params, error }) {
    const data = { types: ['teaching-resource'] };

    try {
      const resource = await store.dispatch('loadResource', {
        id: params.slug,
        types: data.types,
        link: route.path,
        country: app.i18n.locale,
      });

      data.title = resource.name || resource.meta_title;
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      customText: '',
      ExperimentTypes,
      title: 'Resource',
      description: 'A teaching resource from Teach Starter',
    };
  },
  head() {
    return {
      title: this.title,
      category: {},
      meta: this.$metaPropertiesResource(this.resource),
      link: [
        ...this.getPreloadedImageLinks(this.resource),
        ...this.$hrefLangsResource(this.resource.linked, this.resource.link, this.resource.country),
      ],
    };
  },
  jsonld() {
    return this.$jsonSchemaResource(this.resource);
  },
  computed: {
    ...mapGetters([
      'resource',
      'isAuthenticated',
      'loggedInUser',
      'experiments',
      'userDownloadPermissions',
    ]),
    isFreeOrGuestUser() {
      return !this.isAuthenticated || this.loggedInUser.subscription.code === 'free';
    },
  },
  mounted() {
    this.$mzAnalyticsApi({
      event: 'pageView',
      name: 'resource_page',
      productArea: 'resources',
      entityType: this.resource.type,
      entityId: this.resource.id,
    });
    if (this.resource.event_params_intercom) {
      const metadata = this.$constructIntercomResourceParams(this.resource);
      if (typeof this.$intercom !== 'undefined') {
        this.$intercom.trackEvent('Resource Viewed', metadata);

        if (this.loggedInUser) {
          this.$intercomIdentify();
        }
      }
    }
  },
  methods: {
    // Check that resource has a letter set tag, has one file that is a pdf and is at least 62 pages (this covers a-z, A-Z, 0-9)
    checkAllowsCustomText(resource) {
      return resource.tags.some(tag => {
        return (
          /Letter Sets/.test(tag.name) &&
          resource.files.length === 1 &&
          resource.files[0].format === 'pdf' &&
          resource.page_count >= 62
        );
      });
    },
    updateCustomText(customText) {
      this.customText = customText;
    },
    getPreloadedImageLinks(resource) {
      if ((resource?.media?.length ?? 0) <= 0) {
        return [];
      }
      const firstImage = resource.media[0];
      const firstImageLinks = [
        firstImage.large?.src
          ? {
              rel: 'preload',
              href: firstImage.large.src,
              as: 'image',
              media: '(min-width: 1121px)',
            }
          : null,
        firstImage.medium?.src
          ? {
              rel: 'preload',
              href: firstImage.medium.src,
              as: 'image',
              media: '(max-width: 1120px)',
            }
          : null,
        firstImage.thumbnail?.src
          ? { rel: 'preload', href: firstImage.thumbnail.src, as: 'image' }
          : null,
      ];
      const otherImages = resource.media.slice(1);
      const otherImageLinks = otherImages.map(image => {
        return image.medium?.src ? { rel: 'preload', href: image.medium.src, as: 'image' } : null;
      });

      return [...firstImageLinks, ...otherImageLinks].filter(Boolean);
    },
  },
};
</script>

<style scoped lang="scss">
.header {
  grid-area: header;
}
.thumbnail {
  grid-area: thumbnail;
}
.content {
  grid-area: content;
}
.links {
  grid-area: links;
}
.recommended {
  grid-area: recommended;
}
.section-comments {
  grid-area: comments;
}
.section .section-actions {
  padding-bottom: 0 !important;
}
</style>
